
// User Management Web Component
class UserManagement extends HTMLElement {
  constructor() {
    super();
    this.users = [];
    this.isLoading = false;
    this.currentUser = null;
    this.isLoadingUsers = false; // Prevent multiple simultaneous calls
    this.currentPage = 1;
    this.totalPages = 1;
    this.expandedUsers = new Set(); // Track which users have expanded friends
    this.userFriends = new Map(); // Cache friends for each user
    this.loadingFriends = new Set(); // Track which users are loading friends
  }

  connectedCallback() {
    this.render();
    this.setupAuthListener();

    // If we already have a current user (e.g., setCurrentUser was called before connectedCallback),
    // try to load users after a brief delay to ensure DOM is ready
    if (this.currentUser && this.currentUser.role === 'admin') {
      setTimeout(() => {
        if (!this.isLoadingUsers && this.currentUser && this.currentUser.role === 'admin') {
          this.loadUsers();
        }
      }, 100);
    }
  }

  disconnectedCallback() {
    // Remove event listener when component is removed
    if (this.handleAuthStatusLoaded) {
      document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    }
  }

  setupAuthListener() {
    // Listen for centralized auth status
    this.handleAuthStatusLoaded = (e) => {
      const { user, authenticated } = e.detail;

      if (authenticated && user && user.role === 'admin') {
        this.currentUser = user;
        // Only load users if component is visible
        if (this.isVisible()) {
          this.loadUsers();
        }
      } else {
        this.currentUser = null;
      }
    };

    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
  }

  isVisible() {
    // Check if this component is currently visible on the page
    // offsetParent is null if the element or any parent is hidden
    if (this.offsetParent === null) {
      return false;
    }
    // Also check if display is explicitly set to none
    const style = window.getComputedStyle(this);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  render() {
    this.innerHTML = `
      <div class="user-management">
        <div class="user-header">
          <h2>User Management</h2>
          <p>Manage user accounts and roles</p>
        </div>

        <div class="user-content">
          ${this.renderContent()}
        </div>

        ${this.renderPagination()}
      </div>
    `;

    this.attachEventListeners();
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading users...</div>
        </div>
      `;
    }

    if (this.users.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">👥</div>
          <h3>No users found</h3>
          <p>There are no users in the system.</p>
        </div>
      `;
    }

    return `
      <div class="user-list">
        ${this.users.map(user => this.renderUser(user)).join('')}
      </div>
    `;
  }

  renderUser(user) {
    const isExpanded = this.expandedUsers.has(user.id);
    const friends = this.userFriends.get(user.id) || [];
    const isLoadingFriends = this.loadingFriends.has(user.id);
    const createdDate = new Date(user.created_at).toLocaleDateString();
    const roleBadge = user.role === 'admin' ? '<span class="role-badge admin">Admin</span>' : '<span class="role-badge user">User</span>';
    const verifiedBadge = user.email_verified ? '<span class="verified-badge">✓ Verified</span>' : '<span class="verified-badge unverified">✗ Unverified</span>';

    return `
      <div class="user-item">
        <div class="user-main">
          <div class="user-info">
            <div class="user-header-row">
              <h3 class="user-username">${this.escapeHtml(user.username)}</h3>
              ${roleBadge}
              ${verifiedBadge}
            </div>
            <div class="user-details">
              <div class="user-detail">
                <span class="detail-label">Email:</span>
                <span class="detail-value">${this.escapeHtml(user.email)}</span>
              </div>
              ${user.first_name || user.last_name ? `
                <div class="user-detail">
                  <span class="detail-label">Name:</span>
                  <span class="detail-value">${this.escapeHtml((user.first_name || '') + ' ' + (user.last_name || '')).trim() || 'N/A'}</span>
                </div>
              ` : ''}
              <div class="user-detail">
                <span class="detail-label">User ID:</span>
                <span class="detail-value">${user.id}</span>
              </div>
              <div class="user-detail">
                <span class="detail-label">Joined:</span>
                <span class="detail-value">${createdDate}</span>
              </div>
            </div>
          </div>
          <button class="toggle-friends-btn" data-user-id="${user.id}" ${isLoadingFriends ? 'disabled' : ''}>
            ${isExpanded ? '▼' : '▶'} Friends
          </button>
        </div>
        ${isExpanded ? `
          <div class="user-friends">
            ${isLoadingFriends ? `
              <div class="friends-loading">
                <div class="spinner-small"></div>
                <span>Loading friends...</span>
              </div>
            ` : friends.length > 0 ? `
              <div class="friends-list">
                ${friends.map(friend => `
                  <div class="friend-item">
                    <span class="friend-username">${this.escapeHtml(friend.username)}</span>
                    <span class="friend-email">${this.escapeHtml(friend.email)}</span>
                    ${friend.first_name || friend.last_name ? `
                      <span class="friend-name">${this.escapeHtml((friend.first_name || '') + ' ' + (friend.last_name || '')).trim()}</span>
                    ` : ''}
                  </div>
                `).join('')}
              </div>
            ` : `
              <div class="friends-empty">
                <span>No friends</span>
              </div>
            `}
          </div>
        ` : ''}
      </div>
    `;
  }

  renderPagination() {
    if (this.totalPages <= 1) {
      return '';
    }

    const startPage = Math.max(1, this.currentPage - 2);
    const endPage = Math.min(this.totalPages, this.currentPage + 2);
    const pages = [];

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return `
      <div class="pagination">
        <div class="pagination-info">
          Page ${this.currentPage} of ${this.totalPages}
        </div>
        
        <div class="pagination-controls">
          <button class="pagination-btn" ${this.currentPage === 1 ? 'disabled' : ''} data-page="${this.currentPage - 1}">
            ← Previous
          </button>
          
          ${pages.map(page => `
            <button class="pagination-btn ${page === this.currentPage ? 'active' : ''}" data-page="${page}">
              ${page}
            </button>
          `).join('')}
          
          <button class="pagination-btn" ${this.currentPage === this.totalPages ? 'disabled' : ''} data-page="${this.currentPage + 1}">
            Next →
          </button>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Pagination event delegation
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains('pagination-btn') && !e.target.disabled) {
        const page = parseInt(e.target.dataset.page);
        if (page && page !== this.currentPage) {
          this.currentPage = page;
          this.loadUsers();
        }
      }

      // Toggle friends
      if (e.target.classList.contains('toggle-friends-btn')) {
        const userId = parseInt(e.target.dataset.userId);
        if (userId) {
          this.toggleFriends(userId);
        }
      }
    });
  }

  async toggleFriends(userId) {
    if (this.expandedUsers.has(userId)) {
      // Collapse
      this.expandedUsers.delete(userId);
      this.updateContent();
    } else {
      // Expand - load friends if not cached
      this.expandedUsers.add(userId);

      if (!this.userFriends.has(userId)) {
        await this.loadUserFriends(userId);
      } else {
        this.updateContent();
      }
    }
  }

  async loadUserFriends(userId) {
    this.loadingFriends.add(userId);
    this.updateContent();

    try {
      const response = await fetch(`/api/admin/users/${userId}/friends`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.userFriends.set(userId, data.friends || []);
      } else {
        throw new Error('Failed to fetch friends');
      }
    } catch (error) {
      window.logger.error('Failed to load user friends:', error);
      this.userFriends.set(userId, []);
    } finally {
      this.loadingFriends.delete(userId);
      this.updateContent();
    }
  }

  async loadUsers() {
    // Prevent multiple simultaneous calls
    if (this.isLoadingUsers) {
      return;
    }

    // Only load if user is admin and component is visible
    if (!this.currentUser || this.currentUser.role !== 'admin') {
      return;
    }

    if (!this.isVisible()) {
      return;
    }

    this.isLoadingUsers = true;
    this.isLoading = true;
    this.updateContent();

    try {
      const params = new URLSearchParams({
        page: this.currentPage,
        limit: 20
      });

      const response = await fetch(`/api/admin/users?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.users = data.users || [];
        this.totalPages = data.pagination?.pages || 1;
        this.currentPage = data.pagination?.page || 1;
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (error) {
      window.logger.error('Failed to load users:', error);
      this.users = [];
      this.showError('Failed to load users. Please try again.');
    } finally {
      this.isLoading = false;
      this.isLoadingUsers = false;
      this.updateContent();
    }
  }

  updateContent() {
    const content = this.querySelector('.user-content');
    if (content) {
      content.innerHTML = this.renderContent();
    }

    const pagination = this.querySelector('.pagination');
    if (pagination) {
      pagination.outerHTML = this.renderPagination();
    }
  }

  showError(message) {
    const content = this.querySelector('.user-content');
    if (content) {
      content.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">❌</div>
          <h3>Error</h3>
          <p>${this.escapeHtml(message)}</p>
        </div>
      `;
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;

    // If we have user data and they're an admin, load users
    // Use a small delay to ensure component is fully connected and visible
    if (this.currentUser && this.currentUser.role === 'admin') {
      // Small delay to ensure DOM is ready, then load users
      setTimeout(() => {
        if (this.currentUser && this.currentUser.role === 'admin' && !this.isLoadingUsers) {
          this.loadUsers();
        }
      }, 50);
    }
  }

  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Register the custom element
customElements.define('user-management', UserManagement);
